import type { Group, Mesh, MeshPhysicalMaterial, MeshStandardMaterial } from 'three';

import type { CutoutManagerConfig, CutoutInfo } from './types';
import type { CutoutInfo as LegacyCutoutInfo } from '../../../model/cutout-info';
import { addLegacyCutoutCfn } from './functions/add-legacy-cutout.cfn';
import { applyCutoutsToMeshCfn } from './functions/apply-cutouts-to-mesh.cfn';
import { createCutoutMeshCfn } from './functions/create-cutout-mesh.cfn';
import { createRectangularCutoutCfn, createCircularCutoutCfn } from './functions/create-cutout-shapes.cfn';
import { generateCutoutsCfn } from './functions/generate-cutouts.cfn';
import { createDebugVisualizationCfn, clearDebugMeshesCfn } from './functions/create-debug-visualization.cfn';

/**
 * CutoutManager handles CSG (Constructive Solid Geometry) operations:
 * - Creating cutouts in countertops
 * - Managing cutout shapes
 * - Applying CSG subtract operations
 *
 * @example
 * ```typescript
 * const cutoutManager = new CutoutManager({
 *   qualitySettings: getQualitySettings(7)
 * });
 *
 * cutoutManager.addCutout({
 *   id: 'sink-cutout',
 *   islandPositionIndex: 0,
 *   points: [new Vector2(-10, -8), new Vector2(10, -8), new Vector2(10, 8), new Vector2(-10, 8)],
 *   depth: 6,
 *   position: { x: 0, y: 36, z: 12 }
 * });
 *
 * const results = cutoutManager.applyCutouts(countertopMesh, 0);
 * ```
 */
export class CutoutManager {
	#config: CutoutManagerConfig;
	#cutouts: CutoutInfo[] = [];
	#debugMeshes: Mesh[] = [];
	#debugGroup: Group | null = null;

	constructor(config: CutoutManagerConfig) {
		this.#config = config;
	}

	/**
	 * Get config (for CFN access)
	 */
	get config(): CutoutManagerConfig {
		return this.#config;
	}

	/**
	 * Enable or disable debug cutout visualization
	 */
	setDebugMode(enabled: boolean): void {
		this.#config.debugCutouts = enabled;

		// Toggle visibility of all debug meshes, except a vent proxy a highlight is holding open --
		// switching the debug view off must not snuff a glow the viewer asked for.
		for (const mesh of this.#debugMeshes) {
			mesh.visible = enabled || mesh.userData.glowing === true;
		}

		// Also update debug group visibility (for any orphaned meshes)
		if (this.#debugGroup) {
			this.#debugGroup.visible = enabled;
		}
	}

	/**
	 * Get debug mode state
	 */
	get debugMode(): boolean {
		return this.#config.debugCutouts ?? false;
	}

	/**
	 * Get debug group for adding to scene
	 */
	get debugGroup(): Group | null {
		return this.#debugGroup;
	}

	/**
	 * Get all cutouts
	 */
	get cutouts(): CutoutInfo[] {
		return [...this.#cutouts];
	}

	/**
	 * Get cutouts for a specific island (legacy positional key)
	 */
	getCutoutsForIsland(islandIndex: number): CutoutInfo[] {
		return this.#cutouts.filter((c) => c.islandPositionIndex === islandIndex);
	}

	/**
	 * Get cutouts for a specific island by stable identity (island.id).
	 * Preferred over the positional variant — scene/array order drifts after
	 * per-island rebuilds, so index-based matching assigns cutouts to the wrong island.
	 */
	getCutoutsForIslandId(islandId: string): CutoutInfo[] {
		return this.#cutouts.filter((c) => c.islandId === islandId);
	}

	/**
	 * Add a cutout (new format)
	 */
	addCutout(cutout: CutoutInfo): void {
		this.#cutouts.push(cutout);
	}

	/**
	 * Add a cutout from legacy format (from model/cutout-info.ts)
	 */
	addLegacyCutout(cutout: LegacyCutoutInfo): void {
		addLegacyCutoutCfn(this, { cutout });
	}

	/**
	 * Remove a cutout by ID
	 */
	removeCutout(id: string): void {
		this.#cutouts = this.#cutouts.filter((c) => c.id !== id);
	}

	/**
	 * Clear all cutouts
	 */
	clearCutouts(): void {
		this.#cutouts = [];
	}

	/**
	 * Clear cutouts for a specific island (legacy positional key)
	 */
	clearCutoutsForIsland(islandIndex: number): void {
		this.#cutouts = this.#cutouts.filter((c) => c.islandPositionIndex !== islandIndex);
	}

	/**
	 * Clear cutouts for a specific island by stable identity (island.id).
	 * Used by per-island rebuilds so a concurrent rebuild of another island
	 * does not wipe this island's freshly-collected cutouts.
	 */
	clearCutoutsForIslandId(islandId: string): void {
		this.#cutouts = this.#cutouts.filter((c) => c.islandId !== islandId);
	}

	/**
	 * Apply cutouts to a mesh using CSG
	 * CSG module is lazy-loaded on first use.
	 */
	async applyCutoutsToMesh(
		mesh: Mesh,
		islandIndex: number,
		material?: MeshStandardMaterial | MeshPhysicalMaterial
	): Promise<Mesh> {
		return applyCutoutsToMeshCfn(this, { mesh, islandIndex, material });
	}

	/**
	 * Create a cutout mesh from cutout info (matching legacy behavior)
	 */
	createCutoutMesh(cutout: CutoutInfo): Mesh {
		return createCutoutMeshCfn({ cutout });
	}

	/**
	 * Create a rectangular cutout
	 */
	createRectangularCutout(
		id: string,
		islandIndex: number,
		width: number,
		height: number,
		depth: number,
		position: { x: number; y: number; z: number }
	): CutoutInfo {
		return createRectangularCutoutCfn({ id, islandIndex, width, height, depth, position });
	}

	/**
	 * Create a circular cutout (approximated with segments)
	 */
	createCircularCutout(
		id: string,
		islandIndex: number,
		radius: number,
		depth: number,
		position: { x: number; y: number; z: number },
		segments = 32
	): CutoutInfo {
		return createCircularCutoutCfn({ id, islandIndex, radius, depth, position, segments });
	}

	/**
	 * Generate cutouts for all islands (matching legacy behavior)
	 * This unions all meshes of each type and then subtracts cutouts from both
	 * surrounds AND countertops.
	 * CSG module is lazy-loaded on first use.
	 */
	async generateCutouts(
		islands: Group[],
		onCompletion?: () => void
	): Promise<void> {
		return generateCutoutsCfn(this, { islands, onCompletion });
	}

	/**
	 * Create debug visualization meshes for all cutouts
	 * @deprecated Use createDebugVisualizationFromData instead
	 */
	createDebugVisualization(islands: Group[]): Group {
		return this.createDebugVisualizationFromData(this.#cutouts, islands);
	}

	/**
	 * Create debug visualization meshes from cutout data
	 * This should be called AFTER generateCutouts with stored cutout data
	 * Debug meshes are added directly to island groups for correct positioning
	 */
	createDebugVisualizationFromData(cutouts: CutoutInfo[], islands: Group[]): Group {
		// Retire only the debug meshes for the island(s) being regenerated so
		// sibling islands keep theirs (a full clear previously wiped every
		// island's debug meshes on any per-island rebuild).
		const regeneratedIslandIds = new Set(
			islands.map((i) => i.userData?.id as string | undefined).filter(Boolean) as string[]
		);
		const toRemove: Mesh[] = [];
		const retained: Mesh[] = [];
		for (const mesh of this.#debugMeshes) {
			const meshIslandId = mesh.userData?.islandId as string | undefined;
			if (!meshIslandId || regeneratedIslandIds.has(meshIslandId)) {
				toRemove.push(mesh); // regenerated island, or legacy-untagged — retire it
			} else {
				retained.push(mesh);
			}
		}
		clearDebugMeshesCfn(toRemove, null);

		const result = createDebugVisualizationCfn(this, { cutouts, islands });
		this.#debugGroup = result.debugGroup;
		this.#debugMeshes = [...retained, ...result.debugMeshes];
		return result.debugGroup;
	}

	/**
	 * Clear all debug visualization meshes
	 */
	clearDebugMeshes(): void {
		clearDebugMeshesCfn(this.#debugMeshes, this.#debugGroup);
		this.#debugMeshes = [];
	}

	/**
	 * Dispose of resources
	 */
	dispose(): void {
		this.clearCutouts();
		this.clearDebugMeshes();
		this.#debugGroup = null;
	}
}
